import { Button, ModalV2 } from '@components'
import { useBusinessContext } from '@context'
import { fetchBusiness, IBusiness, updateBusiness } from '@query'
import { applyHttpFormErrors } from '@utils'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaBookOpen, FaSave } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { SettingsBooleanValue } from '@pages/SettingsPage/components/SettingsBooleanValue'
import { SettingsDataCard } from '@pages/SettingsPage/components/SettingsDataCard'
import { SettingsReadOnlyField } from '@pages/SettingsPage/components/SettingsReadOnlyField'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { SettingsSwitchField } from '@pages/SettingsPage/components/SettingsSwitchField'

type FormType = Pick<IBusiness, 'has_history_before_nocfo'>

export const OpeningBalanceSettings: React.FC = () => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [isModalVisible, setIsModalVisible] = useState(false)

  const { data } = useQuery({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    refetchOnMount: false
  })

  const { control, handleSubmit, reset, setError, formState } = useForm<FormType>({
    defaultValues: {
      has_history_before_nocfo: data?.has_history_before_nocfo
    }
  })

  useEffect(() => {
    reset({
      has_history_before_nocfo: data?.has_history_before_nocfo
    })
  }, [data?.has_history_before_nocfo, reset])

  const updateMutation = useMutation<IBusiness, unknown, FormType>({
    mutationFn: payload => updateBusiness({ slug }, payload),
    ...{
      onSuccess: async updated => {
        reset({
          has_history_before_nocfo: updated?.has_history_before_nocfo
        })
        await queryClient.invalidateQueries({ queryKey: [slug] })
        await queryClient.invalidateQueries({ queryKey: ['businesses'] })
        toast.success(t('general.changesSaved'))
        setIsModalVisible(false)
      },
      onError: error => applyHttpFormErrors(error, setError)
    }
  })

  return (
    <SettingsSubPageLayout title={t('settings.sections.openingBalances')}>
      <SettingsDataCard
        icon={<FaBookOpen />}
        title={t('settings.sections.openingBalances')}
        action={
          <Button
            isSecondary
            size="sm"
            onClick={() => {
              reset({
                has_history_before_nocfo: data?.has_history_before_nocfo
              })
              setIsModalVisible(true)
            }}
          >
            {t('general.edit')}
          </Button>
        }
      >
        <SettingsReadOnlyField
          layout="row"
          label={t('settings.showOpeningBalance')}
          value={<SettingsBooleanValue value={data?.has_history_before_nocfo} />}
        />
      </SettingsDataCard>

      <ModalV2
        header={t('settings.sections.openingBalances')}
        isVisible={isModalVisible}
        handleOnClose={() => setIsModalVisible(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              type="submit"
              form="opening-balance-settings-form"
              disabled={!formState.isDirty || updateMutation.isPending}
              showSpinner={updateMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form
          id="opening-balance-settings-form"
          onSubmit={handleSubmit(values => updateMutation.mutate(values))}
        >
          <Controller
            name="has_history_before_nocfo"
            control={control}
            render={({ field: { onChange, value } }) => (
              <SettingsSwitchField
                id="has_history_before_nocfo"
                label={t('settings.showOpeningBalance')}
                checked={Boolean(value)}
                onChange={onChange}
              />
            )}
          />
        </form>
      </ModalV2>
    </SettingsSubPageLayout>
  )
}

const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  justify-content: space-between;
  width: 100%;
`
